import {Component} from "@angular/core";
import {ActivatedRoute, Params, Router} from "@angular/router";
import {ProjectService} from "../../project/project.service";
import {CurrencyCodes} from "../../types/currency";
import {ProjectModel, projectModelFactory} from "../../project/project.model";
import {DatePickerOptions} from "../../datepicker/datepicker.component";
import * as moment from "moment";
import {WorkflowStep} from "../workflow-step";
import {WorkflowService} from "../workflow.service";
import {WorkflowStepStatus} from "../models/workflow-step-status.enum";
import {ListEditorService} from "../../list-editor/list-editor.service";
import {ListStructure} from "../../list-editor/list-structure";
import {
  dashboardIssuesData,
  expenseData,
  leaseInstructionsData,
  maiInstructionsData,
  milestoneData,
  qualityChecklistData,
} from "./project-details-data";
import {cloneDeep, isEqual} from "lodash";
import {exposeComponentInDebugMode} from "../../misc/expose-component-in-debug-mode.decorator";
import {handleErrors} from "../../misc/handle-errors.decorator";
import {afterRun} from "../../misc/after-run.decorator";
import {ProjectOffHoldDialogComponent} from "./project-off-hold-dialog.component";
import {DialogService} from "../../bootstrap-modal/dialog.service";
import {SmartsheetService} from "../../smartsheet/smartsheet.service";

@Component({
  selector: "project-list",
  templateUrl: "./project-details.template.pug",
  providers: [
    ProjectService,
  ],
})
@exposeComponentInDebugMode()
export class ProjectDetailsComponent extends WorkflowStep {
  public projectId: any;
  public project: ProjectModel = projectModelFactory();
  public originalProject: ProjectModel = projectModelFactory(); // Just for Dirty Checks
  public currencyCodes: string[] = CurrencyCodes;
  public completionDateOptions: DatePickerOptions;
  public error: Error;
  public partnerPurchaseOrders: string;
  public onHoldMessage: string = null;
  public targetSystem: string;

  // List initialization methods
  public dashboardIssueData = new ListStructure();
  public expenseData = new ListStructure();
  public milestoneData = new ListStructure();
  public qualityChecklistData = new ListStructure();
  public maiInstructionsData = new ListStructure();
  public leaseInstructionsData = new ListStructure();

  private updateDashboardIssueData() {
    this.dashboardIssueData.initialize(
      dashboardIssuesData.generalData,
      dashboardIssuesData.metadata,
      this.listEditorService, // Must be injected
      this.project.dashboardIssues,
    );
  }

  private updateExpenseData() {
    // Set up the currency so that it's marked
    const metadata = cloneDeep(expenseData.metadata);
    metadata.filter(x => x.field === "price").forEach((it) => { it.name = `Price (${this.project.currency})`; });

    this.expenseData.initialize(
      expenseData.generalData,
      metadata,
      this.listEditorService, // Must be injected
      this.project.expenses,
    );
  }

  private updateMilestoneData() {
    this.milestoneData.initialize(
      milestoneData.generalData,
      milestoneData.metadata,
      this.listEditorService, // Must be injected
      this.project.dashboardMilestones,
    );
  }

  private updateQualityChecklistData() {
    this.qualityChecklistData.initialize(
      qualityChecklistData.generalData,
      qualityChecklistData.metadata,
      this.listEditorService, // Must be injected
      this.project.qualityChecklist,
    );
  }

  private updateMaiInstructionsData() {
    this.maiInstructionsData.initialize(
      maiInstructionsData.generalData,
      maiInstructionsData.metadata,
      this.listEditorService, // Must be injected
      this.project.installationInstructions.mai,
    );
  }

  private updateLeaseInstructionsData() {
    this.leaseInstructionsData.initialize(
      leaseInstructionsData.generalData,
      leaseInstructionsData.metadata,
      this.listEditorService, // Must be injected
      this.project.installationInstructions.lease,
    );
  }

  private updateAllLists() {
    this.updateDashboardIssueData();
    this.updateExpenseData();
    this.updateMilestoneData();
    this.updateQualityChecklistData();
    this.updateMaiInstructionsData();
    this.updateLeaseInstructionsData();
  }

  private listsAreDirty() {
    return (
      this.dashboardIssueData.dirty ||
      this.expenseData.dirty ||
      this.milestoneData.dirty ||
      this.qualityChecklistData.dirty ||
      this.maiInstructionsData.dirty ||
      this.leaseInstructionsData.dirty
    );
  }

  private exportAllLists() {
    if (this.dashboardIssueData.dirty) this.project.dashboardIssues = this.dashboardIssueData.export();
    if (this.expenseData.dirty) this.project.expenses = this.expenseData.export();
    if (this.milestoneData.dirty) this.project.dashboardMilestones = this.milestoneData.export();
    if (this.qualityChecklistData.dirty) this.project.qualityChecklist = this.qualityChecklistData.export();
    if (this.maiInstructionsData.dirty) this.project.installationInstructions.mai = this.maiInstructionsData.export();
    if (this.leaseInstructionsData.dirty) {
      this.project.installationInstructions.lease = this.leaseInstructionsData.export();
    }
  }

  /**
   * Converts the Partner Purchase orders from the DB (Array) into a string suitable for display and loads it on ui
   * @param partnerPurchaseOrders {array<string>} Partner Purchase orders from DB
   * @return {void}
   */
  private loadPartnerPurchaseOrders(partnerPurchaseOrders) {
    const localPartnerPurchaseOrders = partnerPurchaseOrders || [];
    this.partnerPurchaseOrders = localPartnerPurchaseOrders.map(po => po.name).join("\n");
  }

  private buildPartnerPurchaseOrders() {
    const poStrings = this.partnerPurchaseOrders.split(/[\r\n]+/) || [];
    return poStrings.map(po => ({name: po}));
  }

  public navigate(queryParams: Params): Promise<boolean> {
    return this.router.navigate(["project-edit"], {queryParams});
  }

  private async getProject(): Promise<ProjectModel> {
    return this.projectService.getProjectById(this.projectId).toPromise();
  }

  @afterRun((self) => { self.loading = false; })
  @handleErrors()
  async loadData(): Promise<void> {
    this.loading = true;

    const project = await this.getProject();
    // Load Partner Purchase Orders
    this.loadPartnerPurchaseOrders(project.partnerPurchaseOrders);
    if (!project.countryId) project.countryId = "";

    this.project = project;
    this.originalProject = cloneDeep(this.project);
    if (this.project.holdDate) {
      const MS_PER_DAY = 24 * 60 * 60 * 1000;
      const now = new Date();
      const holdDate = new Date(this.project.holdDate);
      const timeOnHold = now.getTime() - holdDate.getTime();
      this.onHoldMessage = (timeOnHold < MS_PER_DAY) ?
        `Project has been on hold ${((timeOnHold * 10 / MS_PER_DAY) / 10).toFixed(1)} day(s)` :
        `Project has been on hold ${Math.ceil(timeOnHold / MS_PER_DAY)} day(s)`;
    }

    this.updateAllLists();
  }

  ready() {
    this.error = undefined;
    this.projectId = this.workflowService.workflowConfig.targetId;
    this.targetSystem = this.workflowService.workflowConfig.targetSystem;
    this.loadData();
  }

  constructor(
    private projectService: ProjectService,
    public route: ActivatedRoute,
    public router: Router,
    public workflowService: WorkflowService,
    private listEditorService: ListEditorService,
    private dialogService: DialogService,
    private smartsheetService: SmartsheetService,
  ) {
    super(true);
  }

  @handleErrors()
  async autoSave(): Promise<boolean> {
    this.error = undefined;
    this.exportAllLists();
    this.project.partnerPurchaseOrders = this.buildPartnerPurchaseOrders();

    if (this.originalProject.status === "hold" && this.project.status !== "hold") {
      const incrementResult = await this.dialogService.showDialog(
        ProjectOffHoldDialogComponent,
        {message: this.onHoldMessage, projectName: this.project.name},
        {backdropColor: "rgba(0, 0, 0, 0.5)"},
      );

      if (incrementResult) {
        if (incrementResult.increment > 0) {
          await this.projectService.saveProject({...this.project, estimatesIncrement: incrementResult});
          await this.workflowService.setStepStatus(WorkflowStepStatus.Completed);
          return true;
        }
        // Else fall bask to the normal save outside this if block
      } else {
        // Return back to the parent screen
        return false;
      }
    }

    await this.projectService.saveProject(this.project);
    await this.workflowService.setStepStatus(WorkflowStepStatus.Completed);

    return true;
  }

  cancel(): void {
    this.router.navigate(["projects"]);
  }

  startDateChange($event): void {
    if ($event.type === "dateChanged") {
      const minDate = moment(this.project.startDate).add(1, "day").toDate();
      this.completionDateOptions = new DatePickerOptions({minDate});
    }
  }

  private checkPartnerPurchaseOrdersAreDirty() {
    const newPO = this.buildPartnerPurchaseOrders();
    const oldPO = this.originalProject.partnerPurchaseOrders;
    return !isEqual(newPO, oldPO);
  }

  checkDirty(): boolean {
    return !isEqual(this.project, this.originalProject) ||
      this.listsAreDirty() ||
      this.checkPartnerPurchaseOrdersAreDirty();
  }

  @handleErrors()
  async promptUserForSmartsheetWorkspaceId() {
    const result = await this.smartsheetService.promptUserForSmartsheetWorkspaceId(
      this.projectId,
      !!this.project.smartsheetWorkspace,
    );

    if (!result || !result.smartsheetWorkspace) {
      return;
    }

    this.project.smartsheetWorkspace = result.smartsheetWorkspace.id || null;
  }
}
